import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'

import { Button, H4, H5, Loader, RadioButton, Select, TOAST_TYPE, UploadButton } from '@base'
import { Spacings, Text } from '@styles'
import { strings } from '@constants'
import { updateLocationApi, uploadKYCDocument } from '@data'
import { toggleFileViewer } from '@data/state/action/root'
import { showLoader, showToast } from '@data/state/action'
import {
	ActionButton,
	ActionButtonWapper,
	ButtonIcon,
	ButtonIconWapper,
	FooterWrapper,
	Label,
	PermissionStatusWrapper,
	RadioButtonWrapper,
	RadioInputWapper,
	StepsToAllow,
} from './styles'
import RejectReason from './RejectReason'
import Webcam from './WebCam'
import { ChevronLeftOutlined, ChevronRightOutlined, Lock } from '@material-ui/icons'
import { isClient } from '@utils/adminFilter'

const SectionWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
`

const SectionItemWrapper = styled.div`
	display: flex;
	flex-shrink: unset;
	flex-basis: 100%;
	height: 100%;
	flex-direction: row;
	justify-content: flex-end;
	margin-top: ${({ marginTop }) => marginTop || `${Spacings.SPACING_2B}`};
	padding-left: ${Spacings.SPACING_2B};

	& > div {
		width: 100%;
	}
`

const HeaderWrapper = styled(H5)`
	align-self: center;
	flex: 1;
`

const HeaderSpanWrapper = styled.span`
	color: ${({ theme, color }) => (color ? theme.palette.background[color] : theme.palette.text.greydark)};
	font-size: ${Text.SMALL};
`

const TitleWrapper = styled(H4)`
	margin-bottom: ${Spacings.SPACING_1B};
	margin-top: ${({ marginTop }) => (marginTop ? Spacings.SPACING_6B : 0)};
	padding-left: ${Spacings.SPACING_2B};
	font-weight: bold;
`

const FormHeading = styled(H5)`
	margin: ${Spacings.SPACING_2B} 0;
	padding-right: ${Spacings.SPACING_32B};
	min-width: 200px;
`

const DocumentWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
`
const ExtraDiv = styled.div`
	margin-right: ${Spacings.SPACING_4B};
`

const RequiredMark = styled.span`
	color: ${({ theme }) => theme.palette.background.red};
	margin-left: 2px;
`

const DocumentSectionLabel = styled(H4)`
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-size: ${Text.M_LARGE};
	font-weight: bold;
	margin-top: ${Spacings.SPACING_4B};
	margin-bottom: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_2B};
	background: linear-gradient(180deg, rgba(234, 241, 250, 1) 100%, rgba(255, 254, 254, 1) 68%);
	color: ${({ theme }) => theme.palette.text.darkblack};
	border-radius: 5px;
`

const SectionUploadCount = styled.span`
	font-size: ${Text.SMALL};
	font-weight: 600;
	color: ${({ theme }) => theme.palette.text.black};
	background: ${({ theme }) => theme.palette.background.black};
	padding: 2px ${Spacings.SPACING_2B};
	border-radius: ${Spacings.SPACING_4B};
`

const AUTH_DOC_OPTIONS = [
	{ value: 'authorisation_letter', label: 'Authorisation letter' },
	{ value: 'board_resolution', label: 'Board resolution' },
	{ value: 'email_confirmation', label: 'Official email confirmation' },
]

const deriveAuthDocType = (kyc) => AUTH_DOC_OPTIONS.find((o) => kyc?.[o.value])?.value ?? null

const Documents = ({ org, nextStep, preStep, adminView, kycStatushandler, selectedStep, setOrgData, update }) => {
	const dispatch = useDispatch()
	const [loading, setLoading] = useState(!adminView)
	const [permission, setPermission] = useState(adminView)
	const [isActiveReason, setIsActiveReason] = useState(false)
	const [activeImageWebcam, setActiveImageWebcam] = useState(false)
	const [geoLocation, setGeoLocation] = useState({ lat: 0, lng: 0 })
	const [authDocType, setAuthDocType] = useState(() => deriveAuthDocType(org?.kyc_documents))

	const _user = useSelector((state) => state.getIn(['user', 'user']))
	useEffect(() => {
		if (!adminView) {
			checkGeoLocationPermission()
		}
	}, [])

	const getCurrentGeoPosition = () => {
		navigator.geolocation.getCurrentPosition(
			(position) => {
				setGeoLocation({ lat: position.coords.latitude, lng: position.coords.longitude })
			},
			(error) => {
				if (error.code === error.PERMISSION_DENIED) {
					console.log('Geolocation is not supported by this device')
				}
			}
		)
	}

	const checkGeoLocationPermission = () => {
		setLoading(true)
		navigator.permissions
			.query({ name: 'geolocation' })
			.then((permissionObject) => {
				setPermission(permissionObject.state === 'granted')

				permissionObject.onchange = () => {
					setPermission(permissionObject.state === 'granted')
					if (permissionObject.state === 'granted') {
						getCurrentGeoPosition()
					}
				}

				if (permissionObject.state !== 'granted') {
					getCurrentGeoPosition()
				}
			})
			.catch((err) => {
				console.log(`Error in permission API: ${err}`)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	const updateUserLocation = () => {
		dispatch(showLoader(true))
		updateLocationApi(geoLocation.lat, geoLocation.lng, 'system', org?.user_details?.organisation_id)
			.then((res) => {
				setOrgData({ ...org, ...res })
			})
			.catch((err) => {
				console.log(err)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const uploadFileHandler =
		(key, objectDocType = null, typeOfProperty = null) =>
		(file) => {
			dispatch(showLoader(true))
			uploadKYCDocument(file, key, org?.user_details?.organisation_id, typeOfProperty)
				.then((res) => {
					if (isClient(_user.role) && geoLocation.lat !== 0) {
						updateUserLocation()
					}
					if (objectDocType !== 'typeOfProperty') dispatch(showToast(true, strings('upload_success'), { type: TOAST_TYPE.SUCCESS }))
					setOrgData({ ...org, ...res })
				})
				.catch((err) => console.log(err))
				.finally(() => {
					dispatch(showLoader(false))
				})
		}

	const downloadDocument = (filePath) => {
		dispatch(toggleFileViewer(true, { files: filePath }))
	}

	const capturePhotoHandler = (img) => {
		urltoFile(img, 'photos.jpeg').then(function (file) {
			uploadFileHandler('photos', null)(file)
		})
	}

	const urltoFile = (url, filename, mimeType) => {
		mimeType = mimeType || (url.match(/^data:([^;]+);/) || '')[1]
		return fetch(url)
			.then(function (res) {
				return res.arrayBuffer()
			})
			.then(function (buf) {
				return new File([buf], filename, { type: mimeType })
			})
	}

	const DocumentViewer = useCallback(({ title, _key, _keyType, filePath, selfAttested = false, optional = false, optionalTitle = null }) => {
		const btnTitle = filePath ? strings('change') : strings('select')
		return (
			<DocumentWrapper>
				<HeaderWrapper>
					{title}
					{!optional && <RequiredMark>*</RequiredMark>}
					{selfAttested && <HeaderSpanWrapper> ({strings('selfattested')})</HeaderSpanWrapper>}
					{/* {optional && <HeaderSpanWrapper color='orangeLight'> ({strings('optional')})</HeaderSpanWrapper>} */}
					{optionalTitle && <HeaderSpanWrapper> ({optionalTitle})</HeaderSpanWrapper>}
				</HeaderWrapper>
				<Button xs type='secondary' disabled={!filePath} onClick={() => downloadDocument(filePath)}>
					{strings('view')}
				</Button>
				<ExtraDiv />
				{_keyType === 'photos' ? (
					<Button
						xs
						onClick={() => setActiveImageWebcam(true)}
						variant={btnTitle === strings('select') ? 'destructive' : null}
						disabled={adminView}
					>
						{btnTitle}
					</Button>
				) : (
					<UploadButton
						xs
						onChange={uploadFileHandler(_key, _keyType)}
						label={btnTitle}
						variant={btnTitle === strings('select') ? 'destructive' : null}
					/>
				)}
			</DocumentWrapper>
		)
	}, [])

	const footerHandler = () => {
		if (adminView) {
			return (
				<FooterWrapper>
					<SectionItemWrapper isFooter>
						<ButtonIconWapper>
							<ButtonIcon
								type='secondary'
								small
								uppercase
								onClick={() => {
									preStep()
								}}
							>
								<ChevronLeftOutlined />
							</ButtonIcon>
							<ButtonIcon
								type='secondary'
								small
								margin={'0px 0px 0px 5px'}
								uppercase
								onClick={() => {
									nextStep()
								}}
							>
								<ChevronRightOutlined />
							</ButtonIcon>
						</ButtonIconWapper>
						<ActionButtonWapper>
							<Button
								xs
								type='secondary'
								variant='destructive'
								onClick={() => {
									setIsActiveReason(true)
								}}
							>
								{strings('reject')}
							</Button>
							<Button
								xs
								type='primary'
								margin='0px 0px 0px 3px'
								onClick={() => {
									kycStatushandler('', selectedStep, true, 'Documents request accept')
								}}
							>
								{strings('accept')}
							</Button>
						</ActionButtonWapper>
					</SectionItemWrapper>
					{isActiveReason && (
						<RejectReason
							kycStatushandler={kycStatushandler}
							setIsActiveReason={setIsActiveReason}
							setpNumber={selectedStep}
							stepName='Documents'
							rejectReasonMsg={org?.kyc_reject_reason?.[2] || ''}
						/>
					)}
				</FooterWrapper>
			)
		}

		return (
			<ActionButton disabled={!checkDocument()} small type='primary' onClick={() => nextStep()}>
				{strings('next')}
			</ActionButton>
		)
	}

	function checkDocument() {
		const kyc = org?.kyc_documents
		const hasAuthDoc = AUTH_DOC_OPTIONS.some((o) => kyc?.[o.value])
		return kyc?.pan && kyc?.gst_certificate && kyc?.cancelled_cheque && hasAuthDoc
	}

	const officeType = (rentedOfc) => {
		uploadFileHandler('type_of_property', 'typeOfProperty', rentedOfc ? 'rental' : 'owned')(null)
	}

	const renderDocuments = () => {
		const kyc = org?.kyc_documents
		const isRental = kyc?.office_documents?.type_of_property === 'rental'

		const mandatoryUploaded = [
			kyc?.pan,
			kyc?.gst_certificate,
			kyc?.cancelled_cheque,
			AUTH_DOC_OPTIONS.find((o) => kyc?.[o.value]) ? true : null,
		].filter(Boolean).length
		const mandatoryTotal = 4

		const optionalDocValues = [
			kyc?.aadhaar,
			kyc?.passport_photo,
			kyc?.gst_r2_a,
			kyc?.gst_r3_b,
			kyc?.gst_r1,
			kyc?.bank_statement,
			kyc?.other_documents,
			kyc?.office_documents?.photos,
			kyc?.office_documents?.utility_bill,
			...(isRental ? [kyc?.office_documents?.rent_agreement, kyc?.office_documents?.landlord_noc] : []),
		]
		const optionalUploaded = optionalDocValues.filter(Boolean).length
		const optionalTotal = optionalDocValues.length

		return (
			<SectionWrapper>
				<DocumentSectionLabel>
					Mandatory
					<SectionUploadCount>
						{mandatoryUploaded} / {mandatoryTotal} uploaded
					</SectionUploadCount>
				</DocumentSectionLabel>

				<TitleWrapper>{strings('msg_upload_document_only', '')}</TitleWrapper>
				<SectionItemWrapper>
					<DocumentViewer _key='pan' filePath={org?.kyc_documents?.pan} title={strings('pan')} selfAttested />
				</SectionItemWrapper>


				
				<SectionItemWrapper>
					<DocumentViewer
						_key='gst_certificate'
						filePath={org?.kyc_documents?.gst_certificate}
						title={strings('gst', 'certificate')}
						selfAttested
					/>
				</SectionItemWrapper>
				<SectionItemWrapper>
					<DocumentViewer
						key='cancelledCheque'
						_key='cancelled_cheque'
						filePath={org?.kyc_documents?.cancelled_cheque}
						title={strings('cancelled', 'cheque')}
					/>
				</SectionItemWrapper>
				<SectionItemWrapper>
					<Select
						id='auth-doc-type'
						label={
							<>
								Authorization letter / BR / Official email confirmation <RequiredMark>*</RequiredMark>
							</>
						}
						placeholder='Select Authorization letter / BR / Official email confirmation'
						data={AUTH_DOC_OPTIONS}
						primaryKey='value'
						displayKey='label'
						value={AUTH_DOC_OPTIONS.find((o) => o.value === authDocType) ?? {}}
						onChange={(selected) => setAuthDocType(selected.value)}
						disabled={!!org?.kyc_documents?.[authDocType]}
					/>
				</SectionItemWrapper>
				{authDocType && (
					<SectionItemWrapper>
						<DocumentViewer
							key={authDocType}
							_key={authDocType}
							filePath={org?.kyc_documents?.[authDocType]}
							title={AUTH_DOC_OPTIONS.find((o) => o.value === authDocType)?.label}
						/>
					</SectionItemWrapper>
				)}

				<DocumentSectionLabel optional>
					Optional
					<SectionUploadCount>
						{optionalUploaded} / {optionalTotal} uploaded
					</SectionUploadCount>
				</DocumentSectionLabel>

				<TitleWrapper>{strings('msg_upload_document_only', '')}</TitleWrapper>
				<SectionItemWrapper>
					<DocumentViewer
						optional
						_key='aadhaar'
						filePath={org?.kyc_documents?.aadhaar}
						title={strings('aadhaar')}
						selfAttested
					/>
				</SectionItemWrapper>
				<SectionItemWrapper>
					<DocumentViewer
						optional
						_key='passport_photo'
						filePath={org?.kyc_documents?.passport_photo}
						title={strings('passport', 'photo')}
					/>
				</SectionItemWrapper>

				<SectionItemWrapper>
					<DocumentViewer
						optional
						_key='gst_r2a'
						filePath={org?.kyc_documents?.gst_r2_a}
						title={strings('gstr2a')}
						optionalTitle='Last 2months'
					/>
				</SectionItemWrapper>
				<SectionItemWrapper>
					<DocumentViewer
						optional
						_key='gst_r3b'
						filePath={org?.kyc_documents?.gst_r3_b}
						title={strings('gstr3b')}
						optionalTitle='Past 6months'
					/>
				</SectionItemWrapper>
				<SectionItemWrapper>
					<DocumentViewer _key='gst_r1' filePath={org?.kyc_documents?.gst_r1} title={strings('gstr1')} optional />
				</SectionItemWrapper>
				<SectionItemWrapper>
					<DocumentViewer
						optional
						key='bank_statement'
						_key='bank_statement'
						filePath={org?.kyc_documents?.bank_statement}
						title={strings('bank', 'statements')}
					/>
				</SectionItemWrapper>
				<SectionItemWrapper>
					<DocumentViewer
						optional
						key='otherDocuments'
						_key='other_documents'
						filePath={org?.kyc_documents?.other_documents}
						title={strings('other', 'documents')}
					/>
				</SectionItemWrapper>

				<TitleWrapper marginTop>{strings('msg_upload_document_only', '')}</TitleWrapper>
				<SectionItemWrapper>
					<FormHeading>{strings('office', 'type')}</FormHeading>
					<RadioInputWapper>
						<RadioButtonWrapper>
							<RadioButton
								id='ofc-permanent-radio'
								size='1.125'
								checked={org?.kyc_documents?.office_documents?.type_of_property === 'owned'}
								value='owned'
								onChange={(e) => officeType(false)}
							/>
							<Label htmlFor='ofc-permanent-radio'>{strings('owned')}</Label>
						</RadioButtonWrapper>
						<RadioButtonWrapper>
							<RadioButton
								id='ofc-rented-radio'
								size='1.125'
								checked={org?.kyc_documents?.office_documents?.type_of_property === 'rental'}
								value='rental'
								onChange={(e) => officeType(true)}
							/>
							<Label htmlFor='ofc-rented-radio'>{strings('rental')}</Label>
						</RadioButtonWrapper>
					</RadioInputWapper>
				</SectionItemWrapper>
				{!activeImageWebcam && (
					<SectionItemWrapper>
						<DocumentViewer
							optional
							_key='officeDocuments'
							_keyType='photos'
							filePath={org?.kyc_documents?.office_documents?.photos}
							title={strings('office', 'photos')}
						/>
					</SectionItemWrapper>
				)}
				{activeImageWebcam && (
					<Webcam
						org={org}
						adminView={adminView}
						setActiveImageWebcam={setActiveImageWebcam}
						requesType='photos'
						capturePhotoHandler={capturePhotoHandler}
					/>
				)}
				<SectionItemWrapper>
					<DocumentViewer
						optional
						_key='utility_bill'
						_keyType='utility_bill'
						filePath={org?.kyc_documents?.office_documents?.utility_bill}
						title={strings('utility', 'bill')}
						optionalTitle={strings('all_utility')}
					/>
				</SectionItemWrapper>
				{org?.kyc_documents?.office_documents?.type_of_property === 'rental' && (
					<>
						<SectionItemWrapper>
							<DocumentViewer
								optional
								_key='rent_agreement'
								_keyType='rent_agreement'
								filePath={org?.kyc_documents?.office_documents?.rent_agreement}
								title={strings('rent_agreement')}
								selfAttested
							/>
						</SectionItemWrapper>
						<SectionItemWrapper>
							<DocumentViewer
								optional
								_key='landlord_noc'
								_keyType='landlord_noc'
								filePath={org?.kyc_documents?.office_documents?.landlord_noc}
								optionalTitle={strings('noc')}
								title={strings('owner_noc')}
							/>
						</SectionItemWrapper>
					</>
				)}
			</SectionWrapper>
		)
	}

	const renderContent = () => {
		if (loading) {
			return (
				<PermissionStatusWrapper>
					<H4>Checking location permission</H4>
					<br />
					<Loader size={2} />
				</PermissionStatusWrapper>
			)
		}

		if (!permission) {
			return (
				<PermissionStatusWrapper>
					<H4>Please allow location permission from browser settings</H4>
					<StepsToAllow>
						<H5>
							1. Click the <Lock /> icon in the browser's address bar
						</H5>
						<H5>2. Turn on location</H5>
					</StepsToAllow>
				</PermissionStatusWrapper>
			)
		}

		return (
			<>
				{renderDocuments()}
				{footerHandler()}
			</>
		)
	}

	return renderContent()
}

export default Documents
